"use client"

import { useEffect, useState } from "react"
import LanguageSwitcher from "../../Components/LanguageSwitcher"
import { useTranslations } from "../../i18n/useTranslations"
import { useLocale } from "../../i18n/localeContext"
import { getHeader } from "../../Api/content"
import { Skeleton } from "antd"
import { StrapiError } from '../../Inicio/Pages/StrapiError'

export default function NavLinks() {
  const { t } = useTranslations()
  const { locale } = useLocale()

  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [header, setHeader] = useState(null)
  const [isLoadingContent, setIsLoadingContent] = useState(true)
  const [hasError, setHasError] = useState(false)       // 👈 añadido
  const [retryIndex, setRetryIndex] = useState(0)       // 👈 añadido

  useEffect(() => {
    let cancel = false
    setHasError(false)
    setIsLoadingContent(true)

    const fail = () => {
      if (cancel) return
      setHeader(null)
      setIsLoadingContent(false)
      setHasError(locale === 'en') // se muestra solo en inglés
    }

    const timeoutId = setTimeout(fail, 1000)

    getHeader(locale)
      .then((res) => {
        if (cancel) return
        const data = res?.data?.attributes || res?.data || res
        setHeader(data)
        setIsLoadingContent(false)
      })
      .catch(() => {
        if (cancel) return
        fail()
      })
      .finally(() => {
        clearTimeout(timeoutId)
      })

    return () => {
      cancel = true
      clearTimeout(timeoutId)
    }
  }, [locale]) // solo locale, no retryIndex para evitar loops

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen)
  }

  const closeMenu = () => {
    setIsMenuOpen(false)
  }

  // ⏳ Loading state (Skeleton)
  if (isLoadingContent) {
    return (
      <div className="nav-container">
        {/* Hamburger button skeleton */}
        <Skeleton.Button style={{ width: 30, height: 30 }} active />
        
        {/* Navigation menu skeleton */}
        <ul className="nav-ul">
          <li>
            <Skeleton.Input style={{ width: 120, height: 20 }} active />
          </li>
          <li>
            <Skeleton.Input style={{ width: 140, height: 20 }} active />
          </li>
          <li>
            <Skeleton.Input style={{ width: 160, height: 20 }} active />
          </li>
        </ul>

        {/* Language switcher skeleton */}
        <div className="nav-language-switcher">
          <div className="language-switcher-container">
            <Skeleton.Button style={{ width: 60, height: 30 }} active />
          </div>
        </div>
      </div>
    )
  }
  if (hasError && locale === 'en') {
    return (
      <StrapiError
        onRetry={() => {
          setHasError(false)
          setIsLoadingContent(true)
          setRetryIndex((i) => i + 1)
        }}
        onUseDefault={() => {
          setHasError(false)
          setIsLoadingContent(false)
          setHeader(null)
        }}
      />
    )
  }

  try {
    return (
      <div className="nav-container">
        {/* Hamburger button for mobile */}
        <button 
          className="hamburger-button"
          onClick={toggleMenu}
          aria-label="Toggle navigation menu"
        >
          <span className={`hamburger-line ${isMenuOpen ? 'open' : ''}`}></span>
          <span className={`hamburger-line ${isMenuOpen ? 'open' : ''}`}></span>
          <span className={`hamburger-line ${isMenuOpen ? 'open' : ''}`}></span>
        </button>

        {/* Navigation menu */}
        <ul className={`nav-ul ${isMenuOpen ? 'nav-open' : ''}`}>
          {(header?.menu && Array.isArray(header.menu) ? header.menu : []).map((item) => (
            <li key={item.id}>
              <a href={item.url}
                 target={item.openInNewTab ? "_blank" : "_self"}
                 rel="noopener noreferrer"
                 onClick={closeMenu}>
                {item.label}
              </a>
            </li>
          ))}
          {!header?.menu && (
            <>
              <li>
                <a href="https://ucenfotec.ac.cr/" target="_blank" rel="noopener noreferrer" onClick={closeMenu}>
                  {t("pagina_principal")}
                </a>
              </li>
              <li>
                <a href="https://ucenfotec.ac.cr/carreras/" target="_blank" rel="noopener noreferrer" onClick={closeMenu}>
                  {t("oferta_academica")}
                </a>
              </li>
              <li>
                <a href="https://centrodeasistencia.ucenfotec.ac.cr/portal/es/home" target="_blank" rel="noopener noreferrer" onClick={closeMenu}>
                  {t("preguntas_frecuentes")}
                </a>
              </li>
            </>
          )}
        </ul>

        {/* Language switcher */}
        <div className="nav-language-switcher">
          <div className="language-switcher-container">
            <LanguageSwitcher />
          </div>
        </div>
      </div>
    )
  } catch (error) {
    console.error("❌ Error in NavLinks:", error)
    return (
      <div className="nav-container">
        <button 
          className="hamburger-button"
          onClick={toggleMenu}
          aria-label="Toggle navigation menu"
        >
          <span className={`hamburger-line ${isMenuOpen ? 'open' : ''}`}></span>
          <span className={`hamburger-line ${isMenuOpen ? 'open' : ''}`}></span>
          <span className={`hamburger-line ${isMenuOpen ? 'open' : ''}`}></span>
        </button>

        <ul className={`nav-ul ${isMenuOpen ? 'nav-open' : ''}`}>
          <li>
            <a href="https://ucenfotec.ac.cr/" target="_blank" rel="noreferrer" onClick={closeMenu}>
              Página principal
            </a>
          </li>
          <li>
            <a href="https://ucenfotec.ac.cr/carreras/" target="_blank" rel="noreferrer" onClick={closeMenu}>
              Oferta académica
            </a>
          </li>
          <li>
            <a href="https://desk.zoho.com/portal/cenfotec868/es/home" target="_blank" rel="noreferrer" onClick={closeMenu}>
              Preguntas frecuentes
            </a>
          </li>
        </ul>
        <div className="nav-language-switcher">
          <p style={{ color: "white", fontSize: "14px", fontWeight: "bold" }}>❌ Error en Language Switcher</p>
          <p style={{ color: "white", fontSize: "12px" }}>{error.message}</p>
        </div>
      </div>
    )
  }
}
